import { useState, type ChangeEvent, type FocusEvent, type KeyboardEvent } from "react";
import { Field, type FieldInputProps, type FieldMetaState } from "react-final-form";
import OutlinedInput from "../../../../../components/common/OutlinedInput/OutlineInput";
import styles from "./EmailSuggestInput.module.scss";

export interface EmailSuggestInputProps { name: string; label?: string; suggestions: string[]; disabled?: boolean; }

interface EmailComboboxProps {
  input: FieldInputProps<string>;
  meta: FieldMetaState<string>;
  label: string;
  listId: string;
  suggestions: string[];
  disabled: boolean;
}

const EmailCombobox = ({ input, meta, label, listId, suggestions, disabled }: EmailComboboxProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const value = input.value ?? "";
  const query = value.trim().toLowerCase();
  const matches = suggestions.filter((email) => email.toLowerCase().includes(query) && email !== value);
  const expanded = open && !disabled && matches.length > 0;
  const optionId = (index: number): string => `${listId}-option-${index}`;

  const close = (): void => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const pick = (email: string): void => {
    input.onChange(email);
    close();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!matches.length) return;
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + step + matches.length) % matches.length);
      return;
    }
    if (event.key === "Enter" && expanded && activeIndex >= 0 && matches[activeIndex]) {
      event.preventDefault();
      pick(matches[activeIndex]);
      return;
    }
    if (event.key === "Escape" && expanded) {
      event.preventDefault();
      close();
    }
  };

  return (
    <label className={`${styles.field} ${meta.touched && meta.error ? styles.invalid : ""}`}>
      <span>{label}</span>
      <div className={styles.control}>
        <OutlinedInput
          name={input.name}
          value={value}
          type="email"
          placeholder="Enter email address"
          disabled={disabled}
          error={meta.touched ? meta.error : undefined}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            input.onChange(event);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={(event: FocusEvent<HTMLInputElement>) => {
            setOpen(true);
            input.onFocus(event);
          }}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            close();
            input.onBlur(event);
          }}
          onKeyDown={handleKeyDown}
        />
        {expanded ? (
          <ul className={styles.suggestions} id={listId} role="listbox">
            {matches.map((email, index) => (
              <li
                key={email}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className={`${styles.option} ${index === activeIndex ? styles.optionActive : ""}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(email)}
              >
                {email}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </label>
  );
};

export const EmailSuggestInput = ({ name, label = "Email", suggestions, disabled = false }: EmailSuggestInputProps): JSX.Element => {
  const listId = `${name.replace(/[^a-zA-Z0-9_-]/g, "-")}-suggestions`;
  return (
    <Field<string> name={name}>
      {({ input, meta }) => (
        <EmailCombobox input={input} meta={meta} label={label} listId={listId} suggestions={suggestions} disabled={disabled} />
      )}
    </Field>
  );
};

export default EmailSuggestInput;
